import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import classNames from 'classnames';

// components
import { FormattedPrice } from 'dibs-buyer-price';
import ShippingIcon from 'dibs-vg/dist/react/shipping';
import { PdpSharedBlurb } from './PdpSharedBlurb/PdpSharedBlurb';
import {
    deliveryTitle,
    freeShippingTitle,
    freeWhiteGloveShippingTitle,
    whiteGloveShippingTitle,
    shipsFrom,
    shipsFromLocation,
} from '../messages/pdpBlurbMessages';
import { PdpSharedShippingInfo } from '../PdpSharedShippingInfo/PdpSharedShippingInfo';
import { PdpSharedShippingInfoLink } from '../PdpSharedShippingInfo/PdpSharedShippingInfoLink';

// styles
import styles from './PdpSharedBlurbs.scss';

// helpers
import {
    getQuoteRequestStatus,
    isPriceAboveThreshold,
    getShippingPriceAmount,
} from '../helpers/shippingHelpers';
import get from 'lodash.get';

export class PdpSharedBlurbShippingComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = { modalOpen: false };
    }

    getCustomQuoteStatus() {
        const { shipmentQuote } = this.props;
        const hasSubtotalAmount = !!get(shipmentQuote, 'subTotalAmount');

        return {
            customQuoteRequested: get(shipmentQuote, 'isCustom') && !hasSubtotalAmount,
            customQuoteProvided: get(shipmentQuote, 'isCustom') && hasSubtotalAmount,
        };
    }

    getTitleCopy(isFavorable) {
        const { currency, shipmentQuote } = this.props;
        const complimentaryShipping = !!get(shipmentQuote, 'complimentaryShipping');
        const convertedAmountList = get(shipmentQuote, 'buyerSubTotalAmount.convertedAmountList');
        if (complimentaryShipping && isFavorable) {
            return freeWhiteGloveShippingTitle;
        } else if (isFavorable) {
            const amount = getShippingPriceAmount({ convertedAmountList, currency });
            return whiteGloveShippingTitle(<FormattedPrice currency={currency} value={amount} />);
        } else if (complimentaryShipping) {
            return freeShippingTitle;
        } else {
            return deliveryTitle;
        }
    }

    render() {
        const {
            item,
            itemId,
            viewer,
            currency,
            userId,
            user,
            shipmentQuote,
            shipment,
            renderTooltip,
        } = this.props;

        const itemLocation = get(item, 'address.itemAddress');
        const leadTime = get(item, 'attributes.leadTime.0.option.urlLabel');
        const hasPrequote = get(item, 'hasPreQuotedShipping');
        const { customQuoteRequested, customQuoteProvided } = this.getCustomQuoteStatus();
        const showQuoteDetails = customQuoteRequested || customQuoteProvided || hasPrequote;
        const quoteRequestStatus = getQuoteRequestStatus({
            hasPrequote,
            customQuoteProvided,
            customQuoteRequested,
        });
        const complimentaryShipping = !!get(shipmentQuote, 'complimentaryShipping');
        const isWhiteGlove = !!get(shipmentQuote, 'serviceMethod.isWhiteGlove');
        const convertedAmountList = get(shipmentQuote, 'buyerSubTotalAmount.convertedAmountList');
        const isFavorable = isWhiteGlove && !isPriceAboveThreshold({ convertedAmountList });
        const titleCopy = this.getTitleCopy(isFavorable);
        return (
            <PdpSharedBlurb IconComponent={ShippingIcon}>
                <span
                    className={classNames(styles.title, {
                        [styles.titleOnSeparateLine]: complimentaryShipping || isFavorable,
                    })}
                >
                    {titleCopy}
                </span>
                <PdpSharedShippingInfo
                    currency={currency}
                    shipmentQuote={shipmentQuote}
                    shipment={shipment}
                    leadTime={leadTime}
                    hasPrequote={hasPrequote}
                    quoteRequestStatus={quoteRequestStatus}
                    renderTooltip={renderTooltip}
                    noLineBreaks
                    hidePrice={complimentaryShipping || isFavorable}
                >
                    <PdpSharedShippingInfoLink
                        shipmentQuote={shipmentQuote}
                        currency={currency}
                        userId={userId}
                        item={item}
                        itemId={itemId}
                        user={user}
                        viewer={viewer}
                        showQuoteDetails={showQuoteDetails}
                        quoteRequestStatus={quoteRequestStatus}
                    />
                    <div className={styles.shipsFromContainer}>
                        <span className={styles.shipsFrom}>{shipsFrom}</span>
                        <span>{shipsFromLocation(itemLocation)}</span>
                    </div>
                </PdpSharedShippingInfo>
                <div />
            </PdpSharedBlurb>
        );
    }
}

PdpSharedBlurbShippingComponent.propTypes = {
    currency: PropTypes.string,
    userId: PropTypes.string,
    itemId: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object,
    shipmentQuote: PropTypes.object,
    shipment: PropTypes.object,
    relay: PropTypes.object.isRequired,
    renderTooltip: PropTypes.bool,
};

export const PdpSharedBlurbShipping = createFragmentContainer(PdpSharedBlurbShippingComponent, {
    viewer: graphql`
        fragment PdpSharedBlurbShipping_viewer on Viewer {
            ...PdpSharedShippingInfoLink_viewer
        }
    `,
    user: graphql`
        fragment PdpSharedBlurbShipping_user on User {
            ...PdpSharedShippingInfoLink_user
        }
    `,
    item: graphql`
        fragment PdpSharedBlurbShipping_item on Item {
            ...PdpSharedShippingInfoLink_item
            hasShipmentQuotes
            hasPreQuotedShipping
            itemIsOnHold: isOnHold(buyerId: $userId)
            address {
                itemAddress: displayCityStateCountry(fullCountryName: true)
            }
            attributes {
                leadTime {
                    option {
                        urlLabel
                    }
                }
            }
        }
    `,
    shipmentQuote: graphql`
        fragment PdpSharedBlurbShipping_shipmentQuote on ShipmentQuote {
            isCustom
            isNew
            status
            complimentaryShipping
            buyerSubTotalAmount {
                convertedAmountList {
                    currency
                    amount
                }
            }
            serviceMethod {
                isWhiteGlove
            }
            ...PdpSharedShippingInfoLink_shipmentQuote
            ...PdpSharedShippingInfo_shipmentQuote
            ...PdpSharedBlurbShippingCustomQuote_shipmentQuote
        }
    `,
    shipment: graphql`
        fragment PdpSharedBlurbShipping_shipment on Shipment {
            ...PdpSharedShippingInfo_shipment
        }
    `,
});
